import { useState } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { interpolateTemplate } from "lib/i18n/dictionary";
import { useLocalizedNavigate } from "lib/i18n/navigation";

import {
  canDiscardDraft,
  getNextMessageKind,
  getRemainingForNextMessage,
  getSiteHost,
  isJobActive,
  shouldShowDraft,
} from "./provider-site";
import { ReadyDesignThumb } from "./ready-design-thumb";
import { getCurrentReadyDesign } from "./ready-designs";
import { ReadyDesignsRow } from "./ready-designs-row";
import { SiteAssistantCard } from "./site-assistant-card";
import { SiteConfirmDialog } from "./site-confirm-dialog";
import type { ProviderSite, ReadyDesign } from "./site.documents";
import { SiteHeader } from "./site-header";
import { SiteLanguageCard } from "./site-language-card";
import { useDisableSite, useDiscardSiteDraft, useEnableSite, usePublishSite } from "./site.hooks";
import { notifyError, notifySuccess } from "./site.notify";
import { SITE_CHAT_ROUTE } from "./site.routes";
import { SiteStatusBadge } from "./site-status-badge";
import { SiteVersions } from "./site-versions";
import { openUrl, shareUrl } from "./site.web";
import { useSiteTranslations } from "./use-site-translations";

// A site that exists: its address and state, the draft to publish, the design, the ways to change
// it, its versions, and turning it off or on.
export function SiteDetails({
  site,
  profileUuid,
  aiDesigner,
  designs,
}: {
  site: ProviderSite;
  profileUuid: string;
  aiDesigner: boolean;
  designs: ReadyDesign[];
}) {
  const { t } = useSiteTranslations();
  const navigate = useLocalizedNavigate();
  const [publish, { loading: isPublishing }] = usePublishSite();
  const [discardDraft, { loading: isDiscarding }] = useDiscardSiteDraft();
  const [disable, { loading: isDisabling }] = useDisableSite();
  const [enable, { loading: isEnabling }] = useEnableSite();
  const [dialog, setDialog] = useState<"discard" | "disable" | null>(null);
  const isWorking = isJobActive(site.activeJob);
  const remaining = getRemainingForNextMessage(site);
  const nextKind = getNextMessageKind(site);
  const currentId = getCurrentReadyDesign(site);
  const currentDesign = designs.find((design) => design.id === currentId) ?? null;
  const draftDesign = designs.find((design) => design.id === site.readyDesign) ?? null;
  const host = getSiteHost(site.url);

  const statusLine =
    site.status === "PUBLISHED"
      ? interpolateTemplate(t.publishedVersion, { version: String(site.publishedVersion) })
      : site.status === "DISABLED"
      ? t.disabledStatus
      : t.notPublished;

  async function run(
    action: () => Promise<unknown>,
    errorTitle: string,
    success?: { title: string; description: string }
  ) {
    try {
      await action();
      if (success) notifySuccess(success.title, success.description, t.title);
    } catch {
      notifyError(errorTitle, t.errors.tryAgain);
    }
  }

  async function onShare() {
    try {
      if ((await shareUrl(site.url)) === "copied") notifySuccess(t.linkCopied, "", t.title);
    } catch {
      notifyError(t.share, t.errors.tryAgain);
    }
  }

  return (
    <Grid container spacing={3} data-testid="provider-site-screen">
      <Grid item xs={12}>
        <SiteHeader title={t.title} action={<SiteStatusBadge site={site} />} />
      </Grid>
      <Grid item xs={12} lg={7}>
        <SoftBox display="flex" flexDirection="column" gap={3}>
          <Card data-testid="provider-site-status">
            <SoftBox p={3} display="flex" flexDirection="column" gap={1.5}>
              <SoftBox display="flex" alignItems="center" gap={1}>
                <SoftBox
                  width="0.625rem"
                  height="0.625rem"
                  borderRadius="50%"
                  bgColor={site.status === "PUBLISHED" ? "success" : "secondary"}
                />
                <SoftTypography variant="button" color="text" fontWeight="regular">
                  {statusLine}
                </SoftTypography>
              </SoftBox>
              <SoftTypography variant="h5" sx={{ wordBreak: "break-all", userSelect: "text" }}>
                {host}
              </SoftTypography>
              {site.status === "PUBLISHED" ? (
                <SoftBox display="flex" gap={1.5}>
                  <SoftButton
                    variant="outlined"
                    color="info"
                    fullWidth
                    onClick={() => openUrl(site.url)}
                    data-testid="provider-site-open-button"
                  >
                    {t.open}
                  </SoftButton>
                  <SoftButton
                    variant="outlined"
                    color="info"
                    fullWidth
                    onClick={() => void onShare()}
                    data-testid="provider-site-share-button"
                  >
                    {t.share}
                  </SoftButton>
                </SoftBox>
              ) : null}
            </SoftBox>
          </Card>

          {shouldShowDraft(site) ? (
            <Card data-testid="provider-site-draft">
              <SoftBox p={3} display="flex" gap={2} alignItems="flex-start">
                {draftDesign ? (
                  <SoftBox width="4rem" flexShrink={0}>
                    <ReadyDesignThumb uri={draftDesign.thumbUrl} crop />
                  </SoftBox>
                ) : null}
                <SoftBox flexGrow={1} display="flex" flexDirection="column" gap={1}>
                  <SoftTypography variant="h6">{t.draft.title}</SoftTypography>
                  <SoftTypography variant="button" color="text" fontWeight="regular">
                    {site.status === "PUBLISHED" ? t.draft.message : t.draft.firstMessage}
                  </SoftTypography>
                  <SoftBox display="flex" gap={1.5} pt={1}>
                    {site.draftPreviewUrl ? (
                      <SoftButton
                        variant="outlined"
                        color="info"
                        fullWidth
                        onClick={() => openUrl(site.draftPreviewUrl as string)}
                        data-testid="provider-site-preview-button"
                      >
                        {t.draft.preview}
                      </SoftButton>
                    ) : null}
                    <SoftButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      disabled={isPublishing || isWorking}
                      onClick={() =>
                        void run(
                          () => publish({ variables: { profileUuid } }),
                          t.errors.publishFailedTitle,
                          { title: t.draft.published, description: t.draft.publishedMessage }
                        )
                      }
                      data-testid="provider-site-publish-button"
                    >
                      {isPublishing ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        t.draft.publish
                      )}
                    </SoftButton>
                  </SoftBox>
                  {canDiscardDraft(site) ? (
                    <SoftButton
                      variant="text"
                      color="error"
                      disabled={isDiscarding || isWorking}
                      onClick={() => setDialog("discard")}
                      data-testid="provider-site-discard-button"
                    >
                      {t.draft.discard}
                    </SoftButton>
                  ) : null}
                </SoftBox>
              </SoftBox>
            </Card>
          ) : null}

          <ReadyDesignsRow current={currentDesign} shown={draftDesign} total={designs.length} />

          {aiDesigner ? (
            <SoftBox display="flex" flexDirection="column" gap={1}>
              <SoftButton
                variant="gradient"
                color="info"
                size="large"
                fullWidth
                onClick={() => navigate(SITE_CHAT_ROUTE)}
                data-testid="provider-site-chat-button"
              >
                {t.changeDesign}
              </SoftButton>
              {remaining !== null ? (
                <SoftTypography variant="caption" color="text" textAlign="center">
                  {interpolateTemplate(
                    nextKind === "BUILD" ? t.remainingBuilds : t.remainingEdits,
                    { count: String(remaining) }
                  )}
                </SoftTypography>
              ) : null}
            </SoftBox>
          ) : null}
        </SoftBox>
      </Grid>

      <Grid item xs={12} lg={5}>
        <SoftBox display="flex" flexDirection="column" gap={3}>
          <SiteAssistantCard siteHost={host} primary={!aiDesigner} />
          <SiteLanguageCard site={site} profileUuid={profileUuid} run={run} />
          <SiteVersions site={site} profileUuid={profileUuid} designs={designs} run={run} />
          {site.status === "PUBLISHED" ? (
            <SoftButton
              variant="text"
              color="error"
              disabled={isDisabling}
              onClick={() => setDialog("disable")}
              data-testid="provider-site-disable-button"
            >
              {t.disable}
            </SoftButton>
          ) : site.status === "DISABLED" ? (
            <SoftButton
              variant="text"
              color="info"
              disabled={isEnabling}
              onClick={() =>
                void run(() => enable({ variables: { profileUuid } }), t.errors.disableFailedTitle)
              }
              data-testid="provider-site-enable-button"
            >
              {t.enable}
            </SoftButton>
          ) : null}
        </SoftBox>
      </Grid>

      <SiteConfirmDialog
        open={dialog === "discard"}
        title={t.draft.discardSheet.title}
        message={t.draft.discardSheet.message}
        confirmLabel={t.draft.discardSheet.confirm}
        cancelLabel={t.draft.discardSheet.keep}
        destructive
        busy={isDiscarding}
        onConfirm={() =>
          void run(
            () => discardDraft({ variables: { profileUuid } }),
            t.errors.discardFailedTitle,
            {
              title: t.draft.discarded,
              description: "",
            }
          ).finally(() => setDialog(null))
        }
        onCancel={() => setDialog(null)}
        testId="provider-site-discard-dialog"
      />
      <SiteConfirmDialog
        open={dialog === "disable"}
        title={t.disableConfirmTitle}
        message={t.disableConfirmMessage}
        confirmLabel={t.disable}
        cancelLabel={t.cancel}
        destructive
        busy={isDisabling}
        onConfirm={() =>
          void run(
            () => disable({ variables: { profileUuid } }),
            t.errors.disableFailedTitle
          ).finally(() => setDialog(null))
        }
        onCancel={() => setDialog(null)}
        testId="provider-site-disable-dialog"
      />
    </Grid>
  );
}
